/**
 * The single Group-ref canonicalizer shared by every backend surface that
 * walks this org's team tree (tech-insights' `team-lint-rollup` /
 * `catalog-hygiene` retrievers, `team-management-backend`'s lineage and
 * authorization logic, and `security-insights-backend`'s hierarchy
 * expansion).
 *
 * The real catalog stores `spec.parent` inconsistently — sometimes a bare
 * name, sometimes a fully-qualified ref, with casing not guaranteed (see
 * AGENTS.md). Each surface previously hand-rolled its own canonicalization and
 * they disagreed on lowercasing and namespace handling, so the same catalog
 * data could produce different team trees depending on which module read it.
 * Route every Group-ref comparison through {@link normalizeGroupRef} instead.
 */
import {
  DEFAULT_NAMESPACE,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';

/** The namespace Backstage assigns to an entity that declares none. */
export { DEFAULT_NAMESPACE };

/**
 * Backstage's legal `metadata.name`/`metadata.namespace` character set. Used to
 * reject refs `parseEntityRef` happily returns but the catalog could never hold.
 */
export const ENTITY_NAME_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]*$/;

/**
 * Backstage's `metadata.name`/`metadata.namespace` length limit. Paired with
 * {@link ENTITY_NAME_PATTERN} everywhere a ref is validated — a name/namespace
 * satisfying the pattern but exceeding this length is still not a legal
 * entity name and could never exist in the catalog.
 */
export const MAX_ENTITY_NAME_LENGTH = 63;

/**
 * The minimal Group shape {@link groupRefOf} needs — a structural subset of a
 * catalog `Entity`, so both full `GroupEntity` objects and lightweight tree
 * nodes are accepted.
 */
export interface GroupRefSubject {
  metadata: { name: string; namespace?: string };
}

/**
 * Canonicalises a Group reference to `group:<namespace>/<name>`, lowercased.
 *
 * Accepts every form this codebase encounters: a bare `metadata.name` (some
 * entities' `spec.parent`), a full `group:default/<name>` ref (other entities'
 * `spec.parent`, and every relation `targetRef`), and a namespaced
 * `group:<ns>/<name>`. A bare name resolves against `defaultNamespace`, which
 * callers pass as the *referring entity's own* namespace — matching Backstage's
 * own `spec.parent` resolution in `BuiltinKindsEntityProcessor`, where a
 * namespaced Group's bare parent means a Group in that same namespace.
 *
 * Returns `undefined` for anything unusable: a non-string, an unparseable ref,
 * a ref for a non-Group kind (e.g. a `user:` owner relation), or one whose
 * parsed namespace/name is not a legal entity name — `parseEntityRef` only
 * splits on the first `:`/`/` and does not validate the parts, so `a/b/c`
 * parses to the nonexistent `group:a/b/c` rather than throwing. Callers must
 * handle `undefined` explicitly rather than letting it flow into a catalog
 * query or comparison.
 */
export function normalizeGroupRef(
  ref: unknown,
  defaultNamespace: string = DEFAULT_NAMESPACE,
): string | undefined {
  if (typeof ref !== 'string' || !ref.trim()) {
    return undefined;
  }
  try {
    const { kind, namespace, name } = parseEntityRef(ref.trim(), {
      defaultKind: 'group',
      defaultNamespace: defaultNamespace || DEFAULT_NAMESPACE,
    });
    if (
      kind.toLowerCase() !== 'group' ||
      !ENTITY_NAME_PATTERN.test(namespace) ||
      !ENTITY_NAME_PATTERN.test(name) ||
      namespace.length > MAX_ENTITY_NAME_LENGTH ||
      name.length > MAX_ENTITY_NAME_LENGTH
    ) {
      return undefined;
    }
    return stringifyEntityRef({ kind, namespace, name });
  } catch {
    return undefined;
  }
}

/** The canonical `group:<namespace>/<name>` ref of a Group entity itself. */
export function groupRefOf(group: GroupRefSubject): string {
  const namespace = group.metadata.namespace?.trim() || DEFAULT_NAMESPACE;
  return stringifyEntityRef({
    kind: 'group',
    namespace,
    name: group.metadata.name,
  });
}
